import React, { Fragment, useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Dialog,
  DialogBackdrop,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { toast } from "react-toastify";
import {
  IAssessmentQuestionDetail,
  getCmsAssessmentById,
  updateCmsAssessment,
  validateCmsAssessmentQuestion,
} from "../../../../api/crmContent.service";
import {
  DeleteIcon,
  DragAndOrderIcon,
  EditIcon,
  PreviewIcon,
} from "../../../../assets/icons/index";
import PreviewModal from "../../questions/previewModal";
import Loader from "../../../shared/loader/loader";
import Constant from "../../../../utils/constant";
import { IUserContentOutletContext } from "./UserContentLayout";
import { buildUpdateAssessmentRequest } from "./assessmentHelpers";

// ---------- Sortable row ----------

interface ISortableRowProps {
  question: IAssessmentQuestionDetail;
  index: number;
  userId: number;
  onPreview: (ref: string, questionId: number, index: number) => void;
  onDelete: (questionId: number, typeId: number) => void;
}

const SortableRow: React.FC<ISortableRowProps> = ({
  question,
  index,
  userId,
  onPreview,
  onDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(question.questionId) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const typeLabel = question.assessmentQuestionTypeId === 3 ? "Teacher" : "PL";

  const handleEdit = () => {
    if (question.assessmentQuestionTypeId === 3) {
      window.open(
        `/crm/users/${userId}/content/questions/${question.questionId}`,
        "_blank"
      );
    } else {
      window.open(`/questions/edit/${question.questionId}`, "_blank");
    }
  };

  const handlePreview = () => {
    const ref =
      question.assessmentQuestionTypeId === 3
        ? `${question.questionId}-teacher`
        : question.learnosityReferenceId ?? "";
    onPreview(ref, question.questionId, index);
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={index % 2 === 0 ? "bg-white" : "bg-gray-100"}
    >
      <td className="px-3 py-2">
        <div
          {...attributes}
          {...listeners}
          style={{ cursor: isDragging ? "grabbing" : "grab" }}
          className="inline-flex items-center justify-center"
        >
          <DragAndOrderIcon className="w-6 h-6" />
        </div>
      </td>
      <td className="px-3 py-2 text-sm text-gray-700">{question.questionId}</td>
      <td className="px-3 py-2 text-sm text-gray-500">{typeLabel}</td>
      <td className="px-3 py-2 text-sm text-gray-500">
        {question.standardCode ? `[${question.standardCode}] ` : ""}
        {question.standardName}
      </td>
      <td className="px-3 py-2 text-sm text-center">
        <div className="flex justify-center gap-2">
          <EditIcon
            title="Edit question"
            onClick={handleEdit}
            className="h-5 w-5 cursor-pointer hover:text-secondary-color transition ease-in-out duration-300"
          />
          <PreviewIcon
            title="Preview question"
            onClick={handlePreview}
            className="h-5 w-5 cursor-pointer hover:text-secondary-color transition ease-in-out duration-300"
          />
          <DeleteIcon
            title="Remove question"
            onClick={() =>
              onDelete(question.questionId, question.assessmentQuestionTypeId)
            }
            className="h-5 w-5 cursor-pointer hover:text-secondary-color transition ease-in-out duration-300"
          />
        </div>
      </td>
    </tr>
  );
};

// ---------- Add Regular Question Modal ----------

interface IAddRegularModalProps {
  userId: number;
  subjectId: number;
  existingQuestions: IAssessmentQuestionDetail[];
  onClose: () => void;
  onAdd: (question: IAssessmentQuestionDetail) => void;
}

const AddRegularQuestionModal: React.FC<IAddRegularModalProps> = ({
  userId,
  subjectId,
  existingQuestions,
  onClose,
  onAdd,
}) => {
  const [questionId, setQuestionId] = useState<string>("");
  const [standardId, setStandardId] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const qId = parseInt(questionId, 10);
    const sId = parseInt(standardId, 10);
    if (!qId || !sId) {
      setError("Please enter valid Question ID and Standard ID.");
      return;
    }
    // Regular questions are type 1 (regular) or type 2 (passage)
    if (
      existingQuestions.some(
        (q) => q.questionId === qId && q.assessmentQuestionTypeId !== 3
      )
    ) {
      setError("This PL question is already on this assessment.");
      return;
    }
    setLoading(true);
    setError("");
    validateCmsAssessmentQuestion(userId, qId, sId, undefined, subjectId)
      .then((res) => {
        onAdd({ ...res.data, sequenceNumber: 0 });
        onClose();
      })
      .catch((err) =>
        setError(err?.response?.data || "Question not found or invalid.")
      )
      .finally(() => setLoading(false));
  };

  return (
    <Transition.Root show as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        onClose={onClose}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={onClose}
                >
                  <span className="sr-only">Close</span>
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="flex border-b pb-3">
                <DialogTitle
                  as="h2"
                  className="text-xl font-semibold text-gray-700"
                >
                  Add PL Question
                </DialogTitle>
              </div>
              <form onSubmit={handleAdd}>
                <div className="flex flex-col gap-4 mt-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Question ID
                    </label>
                    <input
                      type="number"
                      value={questionId}
                      onChange={(e) => setQuestionId(e.target.value)}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color sm:text-sm"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Standard ID
                    </label>
                    <input
                      type="number"
                      value={standardId}
                      onChange={(e) => setStandardId(e.target.value)}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color sm:text-sm"
                    />
                  </div>
                  {error && <p className="text-sm text-red-600">{error}</p>}
                </div>
                <div className="flex pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-4 inline-flex items-center text-sm text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg disabled:opacity-50"
                  >
                    {loading ? "Validating..." : "Add"}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

// ---------- Add Teacher Question Modal ----------

interface IAddTeacherModalProps {
  userId: number;
  existingQuestions: IAssessmentQuestionDetail[];
  onClose: () => void;
  onAdd: (question: IAssessmentQuestionDetail) => void;
}

const AddTeacherQuestionModal: React.FC<IAddTeacherModalProps> = ({
  userId,
  existingQuestions,
  onClose,
  onAdd,
}) => {
  const [teacherQuestionId, setTeacherQuestionId] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const tqId = parseInt(teacherQuestionId, 10);
    if (!tqId) {
      setError("Please enter a valid Teacher Question ID.");
      return;
    }
    if (
      existingQuestions.some(
        (q) => q.questionId === tqId && q.assessmentQuestionTypeId === 3
      )
    ) {
      setError("This teacher question is already on this assessment.");
      return;
    }
    setLoading(true);
    setError("");
    validateCmsAssessmentQuestion(userId, undefined, undefined, tqId)
      .then((res) => {
        onAdd({ ...res.data, sequenceNumber: 0 });
        onClose();
      })
      .catch(() =>
        setError(
          "Teacher question not found or does not belong to this district."
        )
      )
      .finally(() => setLoading(false));
  };

  return (
    <Transition.Root show as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        onClose={onClose}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={onClose}
                >
                  <span className="sr-only">Close</span>
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="flex border-b pb-3">
                <DialogTitle
                  as="h2"
                  className="text-xl font-semibold text-gray-700"
                >
                  Add Teacher Question
                </DialogTitle>
              </div>
              <form onSubmit={handleAdd}>
                <div className="flex flex-col gap-4 mt-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Teacher Question ID
                    </label>
                    <input
                      type="number"
                      value={teacherQuestionId}
                      onChange={(e) => setTeacherQuestionId(e.target.value)}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color sm:text-sm"
                    />
                  </div>
                  {error && <p className="text-sm text-red-600">{error}</p>}
                </div>
                <div className="flex pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-4 inline-flex items-center text-sm text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg disabled:opacity-50"
                  >
                    {loading ? "Validating..." : "Add"}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

// ---------- Main AssessmentEdit ----------

export const AssessmentEdit: React.FC = () => {
  const { userId } = useOutletContext<IUserContentOutletContext>();
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState("");
  const [subjectId, setSubjectId] = useState(0);
  const [subjectName, setSubjectName] = useState("");
  const [assessmentStatusId, setAssessmentStatusId] = useState(0);
  const [sharedTypeId, setSharedTypeId] = useState<number | null>(null);
  const [questions, setQuestions] = useState<IAssessmentQuestionDetail[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItemRef, setPreviewItemRef] = useState("");
  const [questionIdPreview, setQuestionIdPreview] = useState<number>();
  const [questionIndexPreview, setQuestionIndexPreview] = useState<number>();
  const [addRegularOpen, setAddRegularOpen] = useState(false);
  const [addTeacherOpen, setAddTeacherOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const assessmentIdNum = parseInt(assessmentId ?? "0", 10);

  useEffect(() => {
    if (!assessmentIdNum) return;
    setLoading(true);
    getCmsAssessmentById(assessmentIdNum)
      .then((res) => {
        const data = res.data;
        setName(data.name);
        setSubjectId(data.subjectId);
        setSubjectName(data.subjectName);
        setAssessmentStatusId(data.assessmentStatusId);
        setSharedTypeId(data.sharedTypeId ?? null);
        setQuestions(
          (data.questions ?? [])
            .slice()
            .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
        );
      })
      .catch(() => toast.error("Failed to load assessment."))
      .finally(() => setLoading(false));
  }, [assessmentIdNum]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setQuestions((prev) => {
        const oldIndex = prev.findIndex(
          (q) => String(q.questionId) === active.id
        );
        const newIndex = prev.findIndex(
          (q) => String(q.questionId) === over.id
        );
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const handlePreview = (ref: string, questionId: number, index: number) => {
    setPreviewItemRef(ref);
    setQuestionIdPreview(questionId);
    setQuestionIndexPreview(index);
    setPreviewOpen(true);
  };

  const handleDelete = (questionId: number, typeId: number) => {
    setQuestions((prev) =>
      prev.filter(
        (q) =>
          !(
            q.questionId === questionId && q.assessmentQuestionTypeId === typeId
          )
      )
    );
  };

  const handleAddQuestion = (question: IAssessmentQuestionDetail) => {
    setQuestions((prev) => [...prev, question]);
  };

  const navigateToList = () => {
    navigate(
      Constant.crmUserContentAssessments.replace(":userid", String(userId))
    );
  };

  const handleSave = () => {
    if (!name.trim()) {
      toast.error("Name is required.");
      return;
    }
    if (questions.length === 0) {
      toast.error("At least one question is required before saving.");
      return;
    }
    setIsSaving(true);
    updateCmsAssessment(
      userId,
      assessmentIdNum,
      buildUpdateAssessmentRequest(name, subjectId, sharedTypeId, questions)
    )
      .then(() => {
        toast.success("Assessment saved successfully.");
        navigateToList();
      })
      .catch(() => toast.error("Failed to save assessment."))
      .finally(() => setIsSaving(false));
  };

  const questionsForPreview = questions.map((q) => ({
    questionId: q.questionId,
    englishQuestionReferenceId:
      q.assessmentQuestionTypeId === 3
        ? `${q.questionId}-teacher`
        : q.learnosityReferenceId ?? "",
  }));

  return (
    <>
      {(loading || isSaving) && <Loader />}

      <PreviewModal
        show={previewOpen}
        changeValue={(val: boolean) => setPreviewOpen(val)}
        itemRef={previewItemRef}
        isTeacherQuestion={previewItemRef.endsWith("-teacher")}
        questionIdPreview={questionIdPreview}
        questionIndexPreview={questionIndexPreview}
        setQuestionIndexPreview={setQuestionIndexPreview}
        setQuestionIdPreview={setQuestionIdPreview}
        setPreviewId={setPreviewItemRef}
        setQuestionToPreview={() => undefined}
        teacherQuestionOwnerUserId={userId}
        questions={questionsForPreview}
      />

      {addRegularOpen && (
        <AddRegularQuestionModal
          userId={userId}
          subjectId={subjectId}
          existingQuestions={questions}
          onClose={() => setAddRegularOpen(false)}
          onAdd={handleAddQuestion}
        />
      )}

      {addTeacherOpen && (
        <AddTeacherQuestionModal
          userId={userId}
          existingQuestions={questions}
          onClose={() => setAddTeacherOpen(false)}
          onAdd={handleAddQuestion}
        />
      )}

      <div className="flex-1 overflow-auto">
        <div className="h-auto min-h-half w-full px-8 py-6">
          {assessmentStatusId === 3 && (
            <div className="mb-4 flex items-center gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-4 py-2 text-sm text-yellow-800">
              <span className="inline-block w-2 h-2 rounded-full bg-gray-400 flex-shrink-0" />
              This assessment is in <strong>Draft</strong> status. It has not
              been saved from the edit screen yet.
            </div>
          )}
          <div className="flex gap-8">
            <div className="flex flex-col gap-4 w-1/3 shrink-0">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 block w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-color"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Subject
                </label>
                <input
                  type="text"
                  value={subjectName}
                  readOnly
                  className="mt-1 block w-full text-sm border border-gray-200 rounded-md px-3 py-2 bg-gray-50 text-gray-500 cursor-not-allowed"
                />
              </div>
            </div>

            <div className="w-2/3 min-w-0">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={questions.map((q) => String(q.questionId))}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="shadow border-b border-gray-200 sm:rounded-lg mb-4">
                    <table className="min-w-full table-fixed divide-y divide-gray-200">
                      <thead className="bg-primary-color">
                        <tr>
                          <th className="px-3 py-2 font-light text-sm text-left text-white w-10" />
                          <th className="px-3 py-2 w-28 font-light text-sm text-left text-white">
                            Question ID
                          </th>
                          <th className="px-3 py-2 w-24 font-light text-sm text-left text-white">
                            Type
                          </th>
                          <th className="px-3 py-2 font-light text-sm text-left text-white">
                            Standard
                          </th>
                          <th className="px-3 py-2 w-28 font-light text-sm text-center text-white rounded-tr-lg">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {questions.length === 0 ? (
                          <tr>
                            <td
                              colSpan={5}
                              className="px-3 py-4 text-sm text-center text-gray-500"
                            >
                              No questions added yet.
                            </td>
                          </tr>
                        ) : (
                          questions.map((q, i) => (
                            <SortableRow
                              key={q.questionId}
                              question={q}
                              index={i}
                              userId={userId}
                              onPreview={handlePreview}
                              onDelete={handleDelete}
                            />
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </SortableContext>
              </DndContext>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setAddRegularOpen(true)}
                  className="inline-flex items-center text-sm text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                >
                  Add PL Question
                </button>
                <button
                  type="button"
                  onClick={() => setAddTeacherOpen(true)}
                  className="inline-flex items-center text-sm text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                >
                  Add Teacher Question
                </button>
              </div>
            </div>
          </div>

          <div className="flex gap-3 border-t pt-4 mt-6">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center text-sm text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg disabled:opacity-50"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={navigateToList}
              className="py-1.5 px-4 inline-flex items-center text-sm rounded-lg text-gray-700 border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
